import { useState, useCallback, useMemo } from 'react'
import moment from 'moment-timezone'
import { keys } from 'ramda'
import cn from 'classnames'

import DaysGrid from './days-grid'
import WeekdaysGrid from './weekdays-grid'
import MonthSwitcher from './month-switcher'
import type { DaysState } from './use-days-slected-state'
import { dateFormat as dateFormatDefault } from './date-format'

import styles from './plain-calendar-double.module.scss';

/**
 *  Компонент календаря
 */

export type PlainCalendarDoubleProps = {
  className?: string,
  daysSelected?: DaysState,
  dateFormat?: string,
  markedDatesFrom?: string,
  markedDatesTo?: string,
  hideYear?: boolean,
  weekSelection?: boolean,
  onDayClick: (date: string, format: string) => void,
  dateDisabledStatus?: (dayFormatted: string, format: string, unit?: string) => boolean,
}

export const PlainCalendarDouble = ({
  className,
  daysSelected = {},
  dateFormat = dateFormatDefault,
  markedDatesFrom,
  markedDatesTo,
  hideYear,
  weekSelection,
  onDayClick,
  dateDisabledStatus,
}: PlainCalendarDoubleProps) => {
  const [monthDate, setMonthDate] = useState(
    keys(daysSelected)[0]
    || moment().format(dateFormat),
  )

  const monthDate2 = useMemo(() => {
    return moment(monthDate, dateFormat)
      .add(1, 'month')
      .format(dateFormat)
  }, [monthDate, dateFormat])

  const handlePrevMonth = useCallback(() => {
    setMonthDate(date => moment(date, dateFormat)
      .subtract(1, 'month')
      .format(dateFormat))
  }, [dateFormat])

  const handleNextMonth = useCallback(() => {
    setMonthDate(date => moment(date, dateFormat)
      .add(1, 'month')
      .format(dateFormat))
  }, [dateFormat])

  const handleMonth = useCallback((newMonth: number) => {
    setMonthDate(date => moment(date, dateFormat)
      .month(newMonth)
      .format(dateFormat))
  }, [dateFormat])

  const handleYear = useCallback((newYear: number) => {
    setMonthDate(date => moment(date, dateFormat)
      .year(newYear)
      .format(dateFormat))
  }, [dateFormat])

  return (
    <div className={cn(styles.root, className)}>
      <div>
        <MonthSwitcher
          monthDate={monthDate}
          dateFormat={dateFormat}
          onPrev={handlePrevMonth}
          onMonth={handleMonth}
          onYear={handleYear}
          dateDisabledStatus={dateDisabledStatus}
          hideYear={hideYear}
          disableMonthModal
        />
        <WeekdaysGrid className={styles.weekdays} />
        <DaysGrid
          className={styles.days}
          daysSelected={daysSelected}
          onClickDay={onDayClick}
          monthDate={monthDate}
          dateFormat={dateFormat}
          dateDisabledStatus={dateDisabledStatus}
          markedDatesFrom={markedDatesFrom}
          markedDatesTo={markedDatesTo}
          weekSelection={weekSelection}
        />
      </div>
      <div>
        <MonthSwitcher
          monthDate={monthDate2}
          dateFormat={dateFormat}
          onNext={handleNextMonth}
          onMonth={handleMonth}
          onYear={handleYear}
          dateDisabledStatus={dateDisabledStatus}
          hideYear={hideYear}
          disableMonthModal
        />
        <WeekdaysGrid className={styles.weekdays} />
        <DaysGrid
          className={styles.days}
          daysSelected={daysSelected}
          onClickDay={onDayClick}
          monthDate={monthDate2}
          dateFormat={dateFormat}
          dateDisabledStatus={dateDisabledStatus}
          markedDatesFrom={markedDatesFrom}
          markedDatesTo={markedDatesTo}
          weekSelection={weekSelection}
        />
      </div>
    </div>
  )
}
